/* eslint-disable @typescript-eslint/no-unused-vars */
import { Auth } from 'aws-amplify';
import { S3Client, GetObjectCommand, PutObjectCommand, } from '@aws-sdk/client-s3';
import { getSignedUrl as awsGetSignedUrl } from '@aws-sdk/s3-request-presigner';
import { Upload } from '@aws-sdk/lib-storage';
import XhrHttpHandler from './XhrHttpHandler';
const CACHE_DURATION = 60 * 60 * 1000; // sixty minutes
const myHttpHandler = new XhrHttpHandler();
const cache = new Map();
// thanks to https://www.jonmellman.com/posts/singleton-promises
class CachedS3Client {
    constructor() {
        this.client = undefined;
        this.expiryTime = 0;
        this.connectionPromise = null;
    }
    async connectToS3() {
        // console.log('Creating s3 client');
        const creds = await Auth.currentCredentials();
        // if s3 client is not null anoher process has been here and set it
        this.expiryTime = Date.now() + CACHE_DURATION;
        if (!creds) {
            console.log('Not authenticated yet', creds);
        }
        this.client = new S3Client({
            apiVersion: '2006-03-01',
            region: 'us-east-1',
            credentials: creds?.authenticated ? Auth.essentialCredentials(creds) : creds,
            requestHandler: myHttpHandler,
        });
    }
    async connect() {
        this.client = undefined;
        await this.connectToS3();
        return this.client;
    }
    reset() {
        this.client = undefined;
    }
    async getClient() {
        // if there is a client then return it
        if (this.client && Date.now() < this.expiryTime) {
            return this.client;
        }
        // return the promise that will resolve to the client
        return this.connect();
    }
}
/**
 * Gets a signed url for an image and displays it in an image tag
 */
const cachedClient = new CachedS3Client();
export const getS3client = async () => cachedClient.getClient();
export const flushSignedUrlCache = () => {
    cache.clear();
    cachedClient.reset();
};
export const getSignedUrl = async (Bucket, Key, responseContentDisposition) => {
    if (!Bucket || !Key)
        throw Error(`Bucket and and Key are required. Bucket: ${Bucket}, Key: ${Key}`);
    const s3 = await getS3client();
    if (!s3)
        throw Error('Error getting s3Client');
    const command = new GetObjectCommand({
        Bucket,
        Key,
        ResponseContentDisposition: responseContentDisposition || undefined,
    }); // Type assertion to bypass the type checking issue
    const url = await awsGetSignedUrl(s3, command, { expiresIn: 60 * 60 * 8 });
    return url;
};
export const getCachedSignedUrl = async (bucketName, key, responseContentDisposition = '') => {
    const mapKey = `${bucketName}:${key}:${responseContentDisposition}`;
    const entry = cache.get(mapKey);
    if (entry && entry.expiry > Date.now()) {
        return entry.signedUrl;
    }
    // cache it and return it
    const newEntry = {
        signedUrl: await getSignedUrl(bucketName, key, responseContentDisposition),
        expiry: Date.now() + CACHE_DURATION, // 14 1/2 minutes
    };
    cache.set(mapKey, newEntry);
    return newEntry.signedUrl;
};
// eslint-disable-next-line @typescript-eslint/default-param-last
export const getSignedAssetUrl = async (asset, sizeKey = 'original', store, responseContentDisposition = '') => {
    if (!asset) {
        console.log('missing asset');
        return undefined;
    }
    await store.load('asset', asset.key);
    if (!asset.repository || !asset.repository.key) {
        console.log('missing repository', asset);
        return undefined;
    }
    const repository = await store.load('repository', asset.repository.key); // `${asset.organization.id}~${asset.repository.id}`);
    const { bucket } = repository;
    const path = asset?.path ? `${asset.path}/${sizeKey}` : undefined;
    // console.log('signing', path);
    if (!bucket || !path) {
        console.log('missing bucket or path', {
            bucket,
            path,
        });
        return undefined;
    }
    return getCachedSignedUrl(bucket, path, responseContentDisposition);
};
export const putObject = async (Bucket, Key, file, progressCallback) => {
    const s3 = await getS3client();
    if (!s3)
        throw Error('Error getting s3Client');
    if (progressCallback) {
        myHttpHandler.progressListener = (progressEvent) => {
            progressCallback((progressEvent.loaded / progressEvent.total) * 100);
        };
    }
    const data = await s3.send(new PutObjectCommand({
        Bucket,
        Key,
        Body: file,
        ContentType: file.type,
    })); // expires in seconds
    myHttpHandler.progressListener = undefined;
    console.log('Put result: ', data);
    return data;
};
export const putContent = async (Bucket, Key, content, ContentType, progressCallback) => {
    const s3 = await getS3client();
    if (!s3)
        throw Error('Error getting s3Client');
    if (progressCallback) {
        myHttpHandler.progressListener = (progressEvent) => {
            progressCallback((progressEvent.loaded / progressEvent.total) * 100);
        };
    }
    const data = await s3.send(new PutObjectCommand({
        Bucket,
        Key,
        Body: content,
        ContentType,
    })); // expires in seconds
    myHttpHandler.progressListener = undefined;
    console.log('Put result: ', data);
    return data;
};
/** This isnt working for multi-part objects - the operation completes but the file isnt in S3
  * as an added bonus the progress meter isn't very good - you get one update per 5MB.
  */
export const uploadObject = async (Bucket, Key, file, progressCallback) => {
    const s3 = await getS3client();
    if (!s3)
        throw Error('Error getting s3Client');
    try {
        const uploader = new Upload({
            client: s3,
            // queueSize: 2, // optional concurrency configuration
            partSize: 5242880,
            params: {
                Bucket,
                Key,
                Body: file,
                // ContentType: file.type,
            },
        });
        // // START CONTENT-TYPE WORKAROUND https://github.com/aws/aws-sdk-js-v3/issues/1891
        // // (this patch is a modification from the monkey patch on the above issue)
        // // @ts-ignore
        // // eslint-disable-next-line func-names
        // uploader.uploader.initialize = async function () {
        //   // @ts-ignore
        //   // eslint-disable-next-line no-underscore-dangle
        //   this.destination = (await this._initiateUpload({
        //     Bucket: this.destination.Bucket,
        //     Key: this.destination.Key,
        //     // Currently missing, fixes the bug vvv
        //     // eslint-disable-next-line @typescript-eslint/no-explicit-any
        //     ContentType: (this as any).configuration.params.ContentType,
        //     // @ts-ignore
        //   })) as MultiPartIdentity;
        //   return this.destination;
        // };
        // // END CONTENT-TYPE WORKAROUND
        uploader.on('httpUploadProgress', (progress) => {
            console.log(progress);
            if (progress.loaded && progress.total) {
                progressCallback((progress.loaded * 100) / progress.total);
            }
        });
        const result = await uploader.done();
        return result;
    }
    catch (e) {
        console.log(e);
    }
    return null;
};
export const getContentsFromS3 = async (bucketName, key) => {
    try {
        const signedUrl = await getCachedSignedUrl(bucketName, key);
        const response = await fetch(signedUrl);
        if (!response.ok) {
            throw new Error(`Failed to fetch object from S3: ${response.statusText}`);
        }
        const text = await response.text();
        return text;
    }
    catch (error) {
        console.error('Error getting object from S3:', error);
        throw error;
    }
};
//# sourceMappingURL=S3Client.js.map